// Scoreboard — Tab-hold center overlay showing server info + player rankings

import { FP } from "../../engine/fixed.js";
import type { ScoreboardConfig } from "../scoring.js";
import { FONT, PANEL_BG, BORDER, TEXT, TEXT_DIM } from "./theme.js";

const INTS_PER_ENTITY = 7;

export interface ScoreboardInfo {
  serverName: string;
  mapName: string;
  mapCreator?: string;
  modeName: string;
  presetName: string;
}

export class Scoreboard {
  private el: HTMLDivElement;
  private headerEl: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private visible = false;
  private config: ScoreboardConfig;
  private localPlayerId: number;
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;

  constructor(config: ScoreboardConfig, info: ScoreboardInfo, localPlayerId: number) {
    this.config = config;
    this.localPlayerId = localPlayerId;

    this.el = document.createElement("div");
    this.el.style.cssText =
      `position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);` +
      `background:${PANEL_BG};border:1px solid ${BORDER};padding:0;` +
      `min-width:500px;z-index:600;display:none;`;

    // Header
    this.headerEl = document.createElement("div");
    this.headerEl.style.cssText =
      `padding:10px 16px;border-bottom:1px solid ${BORDER};font:12px ${FONT};color:${TEXT};` +
      `display:flex;gap:16px;`;

    const serverName = document.createElement("span");
    serverName.textContent = info.serverName;
    serverName.style.cssText = `font-weight:bold;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;`;

    const mapSpan = document.createElement("span");
    mapSpan.textContent = info.mapCreator ? `${info.mapName} by ${info.mapCreator}` : info.mapName;
    mapSpan.style.color = TEXT_DIM;

    const modeSpan = document.createElement("span");
    modeSpan.textContent = info.modeName;
    modeSpan.style.color = TEXT_DIM;

    const presetSpan = document.createElement("span");
    presetSpan.textContent = info.presetName;
    presetSpan.style.color = TEXT_DIM;

    this.headerEl.append(serverName, mapSpan, modeSpan, presetSpan);
    this.el.appendChild(this.headerEl);

    // Column headers
    const colHeader = document.createElement("div");
    colHeader.style.cssText =
      `display:flex;padding:6px 16px;border-bottom:1px solid ${BORDER};` +
      `font:bold 10px ${FONT};color:${TEXT_DIM};letter-spacing:1px;text-transform:uppercase;`;

    const addColHeader = (text: string, width: string, flex?: boolean) => {
      const s = document.createElement("span");
      s.textContent = text;
      s.style.cssText = flex ? "flex:1;" : `width:${width};text-align:right;`;
      colHeader.appendChild(s);
    };
    addColHeader("#", "30px");
    addColHeader("Player", "", true);
    for (const col of config.columns) addColHeader(col.label, "100px");
    addColHeader("Speed", "70px");
    this.el.appendChild(colHeader);

    // Body (rows rendered dynamically)
    this.bodyEl = document.createElement("div");
    this.bodyEl.style.cssText = `max-height:300px;overflow-y:auto;`;
    this.el.appendChild(this.bodyEl);

    document.body.appendChild(this.el);

    // Tab key listeners
    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Tab") {
        e.preventDefault();
        this.visible = true;
        this.el.style.display = "block";
      }
    };
    this.onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Tab") {
        e.preventDefault();
        this.visible = false;
        this.el.style.display = "none";
      }
    };
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);
  }

  update(entityCount: number, buffer: Int32Array): void {
    if (!this.visible) return;

    // Build player data: id, sort value, speed
    const players: { id: number; speed: number }[] = [];
    for (let i = 0; i < entityCount; i++) {
      const off = 2 + i * INTS_PER_ENTITY;
      const vx = FP.toFloat(buffer[off + 4]);
      const vz = FP.toFloat(buffer[off + 6]);
      players.push({ id: i, speed: Math.sqrt(vx * vx + vz * vz) });
    }

    // Sort: players with scores first (by primary sort value), then unranked by ID
    const sortDir = this.config.columns[0]?.sortDirection === "desc" ? -1 : 1;
    players.sort((a, b) => {
      const aHas = this.config.hasScore(a.id) ? 1 : 0;
      const bHas = this.config.hasScore(b.id) ? 1 : 0;
      if (aHas !== bHas) return bHas - aHas;
      if (aHas && bHas) {
        return (this.config.sortValue(a.id) - this.config.sortValue(b.id)) * sortDir;
      }
      return a.id - b.id;
    });

    // Render rows
    this.bodyEl.innerHTML = "";
    let rank = 1;
    for (const p of players) {
      const isLocal = p.id === this.localPlayerId;
      const hasScore = this.config.hasScore(p.id);

      const row = document.createElement("div");
      row.style.cssText =
        `display:flex;padding:5px 16px;font:12px ${FONT};color:${TEXT};` +
        `border-bottom:1px solid ${BORDER};` +
        (isLocal ? `background:rgba(92,140,180,0.12);` : "");

      // Rank
      const rankSpan = document.createElement("span");
      rankSpan.style.cssText = `width:30px;color:${TEXT_DIM};`;
      rankSpan.textContent = hasScore ? String(rank++) : "\u2014";

      // Player name
      const nameSpan = document.createElement("span");
      nameSpan.style.cssText = "flex:1;";
      nameSpan.textContent = isLocal ? `\u25BA Player ${p.id}` : `Player ${p.id}`;

      row.append(rankSpan, nameSpan);

      // Mode-specific columns
      for (const col of this.config.columns) {
        const val = this.config.columnValue(p.id, col.key);
        const cell = document.createElement("span");
        cell.style.cssText = `width:100px;text-align:right;color:${val === 0 ? TEXT_DIM : TEXT};`;
        cell.textContent = col.format(val);
        row.appendChild(cell);
      }

      // Speed (universal)
      const speedSpan = document.createElement("span");
      speedSpan.style.cssText = `width:70px;text-align:right;color:${TEXT_DIM};`;
      speedSpan.textContent = `${Math.round(p.speed)}`;
      row.appendChild(speedSpan);

      this.bodyEl.appendChild(row);
    }
  }

  destroy(): void {
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
    this.el.remove();
  }
}
